/* Shared data layer for the disputes corpus.
 *
 * The site (app.js) and the Observable notebook (notebook.js) both read the
 * same record and must agree on every derived value — a row's date, its
 * amount, its pill slug. Keeping the shape and the formatters here is what
 * makes "the notebook shows the same table" true rather than aspirational.
 * Pure functions only: no DOM, no fetch, no module state.
 */

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

export const esc = (s) =>
  String(s ?? '').replace(/[&<>"']/g, (c) =>
    ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c],
  );

export const cellOf = (row, name) => row.cells?.[name] ?? null;
export const cellText = (row, name) => cellOf(row, name)?.text ?? '';

export function titleCase(s) {
  const t = String(s ?? '').trim();
  if (!t) return '';
  return t.charAt(0).toUpperCase() + t.slice(1);
}

/** Stable slug for failure-type pill color scale. */
export function failureSlug(label) {
  return String(label || 'uncoded')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '') || 'uncoded';
}

export function fmtDate(iso) {
  if (!iso) return '—';
  const m = String(iso).match(/^(\d{4})-(\d{2})/);
  if (!m) return String(iso);
  const month = MONTHS[Number(m[2]) - 1];
  return month ? `${month} ${m[1]}` : String(iso);
}

export function fmtMoney(n) {
  if (typeof n !== 'number' || !Number.isFinite(n)) return '—';
  const abs = Math.abs(n);
  if (abs >= 1e9) return `$${(n / 1e9).toFixed(2)}B`;
  if (abs >= 1e6) {
    const v = n / 1e6;
    return `$${Number.isInteger(v) || Math.abs(v * 10 - Math.round(v * 10)) < 1e-6 ? Math.round(v) : v.toFixed(1)}M`;
  }
  if (abs >= 1e3) return `$${Math.round(n / 1e3)}K`;
  return `$${n}`;
}

/** First cell present among `names`, so one row shape covers both corpora. */
export const firstCell = (cells, names) => names.map((n) => cells[n]).find(Boolean) ?? null;

/**
 * Kalshi market URLs end in the ticker (…/<series>/<slug>/<ticker>), so derive
 * it from the last path segment. Other venues use opaque slugs, not tickers.
 */
export function marketTicker(url, venue) {
  if (!url) return '';
  const isKalshi = /kalshi/i.test(venue) || /kalshi\.com/i.test(url);
  if (!isKalshi) return '';
  try {
    const last = new URL(url).pathname.replace(/\/+$/, '').split('/').pop();
    return last ? last.toUpperCase() : '';
  } catch {
    return '';
  }
}

/**
 * Supplemental sources from a Notion "Links" cell. Editors can author them
 * three ways and all resolve to {title, url}:
 *   • ⌘K-linked text in Notion  → per-segment hrefs the worker already extracts
 *   • Markdown "[title](url)"    → parsed here (works around API link limits)
 *   • bare URL(s)                → shown with the URL as the title
 */
export function linkSources(cell) {
  if (Array.isArray(cell?.links) && cell.links.length) {
    return cell.links
      .filter((l) => l && l.href)
      .map((l) => ({ t: l.text || l.href, u: l.href }));
  }
  const text = cell?.text || cell?.href || '';
  if (!text) return [];
  const md = /\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/g;
  const out = [];
  let m;
  while ((m = md.exec(text))) out.push({ t: m[1], u: m[2] });
  if (out.length) return out;
  return text
    .split(/[\s,]+/)
    .filter((u) => /^https?:\/\//i.test(u))
    .map((u) => ({ t: u, u }));
}

/** Canonical key so Market Link and Links don't both list the same URL. */
function sourceKey(s) {
  const u = String(s?.u || s?.t || '').trim();
  if (!u) return '';
  try {
    const url = new URL(u);
    url.hash = '';
    let path = url.pathname.replace(/\/+$/, '') || '/';
    return `${url.protocol}//${url.host.toLowerCase()}${path}${url.search}`.toLowerCase();
  } catch {
    return u.toLowerCase();
  }
}

function dedupeSources(list) {
  const seen = new Set();
  const out = [];
  for (const s of list) {
    if (!s || !(s.u || s.t)) continue;
    const key = sourceKey(s);
    if (key && seen.has(key)) continue;
    if (key) seen.add(key);
    out.push(s);
  }
  return out;
}

/** Normalize a raw API row into the public table shape + keep cells for detail. */
export function normalizeRow(raw) {
  const cells = raw.cells ?? {};
  const market =
    cells.Market?.text || cells.Name?.text || cells.Title?.text || raw.title || '';
  const venueRaw = cells.Venue?.text || '';
  const venue = titleCase(venueRaw);
  // Submissions uses Resolution Date / Dispute Amount; the legacy corpus used
  // Month / Notional. Fall back to Submitted so a row always sorts by date.
  const dateCell = firstCell(cells, ['Month', 'Resolution Date', 'Submitted']);
  const dateRaw = dateCell?.value || dateCell?.text || '';
  const date = dateRaw ? String(dateRaw).slice(0, 10) : '';
  const failure = cells['Failure type']?.text || '';
  const amount = firstCell(cells, ['Notional', 'Dispute Amount']);
  const notional = typeof amount?.value === 'number' ? amount.value : null;

  // Sources: the market page first (labelled by its ticker where we can derive
  // one), then each supplemental link the editor curated in "Links". Editors
  // often paste the market URL into both columns — collapse those duplicates.
  let sources;
  if (Array.isArray(raw.sources)) {
    sources = dedupeSources(raw.sources);
  } else {
    sources = [];
    const marketLink = cells['Market Link']?.href || cells['Market Link']?.text || '';
    if (marketLink) {
      const ticker = marketTicker(marketLink, venue);
      sources.push({ t: ticker || 'Market page', u: marketLink, mono: Boolean(ticker) });
    }
    sources.push(...linkSources(cells['Links'] || cells['Link'] || cells['Source']));
    sources = dedupeSources(sources);
  }

  return {
    id: raw.id,
    url: raw.url,
    title: market,
    market,
    venue,
    date,
    failure,
    notional,
    sources,
    cells,
  };
}

export function sortValue(row, key) {
  if (key === 'notional') return row.notional;
  if (key === 'date') return row.date || '';
  return String(row[key] ?? '').toLowerCase();
}

/**
 * Filter + sort, shared so the notebook orders rows identically to the table.
 * `state` is {q, venue, failure, sort, dir}. Rows with no amount sort last
 * regardless of direction; market name then id break ties so the order is
 * stable across reloads.
 */
export function filterSortRows(rows, state) {
  const q = String(state.q ?? '').trim().toLowerCase();
  const list = rows.filter((row) => {
    if (state.venue && row.venue.toLowerCase() !== state.venue.toLowerCase()) return false;
    if (state.failure && row.failure !== state.failure) return false;
    if (!q) return true;
    const hay = `${row.market} ${row.venue} ${row.failure} ${cellText(row, 'Summary')} ${cellText(row, 'What went wrong')}`.toLowerCase();
    return hay.includes(q);
  });

  const dir = state.dir === 'asc' ? 1 : -1;
  return list.slice().sort((a, b) => {
    const av = sortValue(a, state.sort);
    const bv = sortValue(b, state.sort);
    let result = 0;
    if (state.sort === 'notional') {
      if (av == null && bv != null) return 1;
      if (bv == null && av != null) return -1;
      result = av == null && bv == null ? 0 : Number(av) - Number(bv);
    } else {
      result = String(av).localeCompare(String(bv), undefined, {
        numeric: true,
        sensitivity: 'base',
      });
    }
    return (
      result * dir ||
      String(a.market).localeCompare(String(b.market)) ||
      String(a.id).localeCompare(String(b.id))
    );
  });
}
